import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import {
  FiCalendar,
  FiBriefcase,
  FiMapPin,
  FiUsers,
  FiDollarSign,
  FiStar,
  FiMessageSquare,
  FiSend,
  FiUser,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { jobRequisitionService } from "../jobRequisitionService";

const STATUS_COLORS = {
  Draft: { scheme: "gray", bg: "gray.100", color: "gray.700" },
  "Pending Approval": {
    scheme: "amber",
    bg: "orange.100",
    color: "orange.700",
  },
  Approved: { scheme: "green", bg: "green.100", color: "green.700" },
  Rejected: { scheme: "red", bg: "red.100", color: "red.700" },
  "On Hold": { scheme: "orange", bg: "orange.100", color: "orange.700" },
  Cancelled: { scheme: "red", bg: "red.100", color: "red.700" },
  Filled: { scheme: "blue", bg: "blue.100", color: "blue.700" },
};

const PRIORITY_COLORS = {
  Low: { scheme: "gray" },
  Medium: { scheme: "blue" },
  High: { scheme: "orange" },
  Critical: { scheme: "red" },
};

const getStatusColor = (s) => STATUS_COLORS[s] || STATUS_COLORS.Draft;
const getPriorityColor = (p) => PRIORITY_COLORS[p] || PRIORITY_COLORS.Medium;

const fmt = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const fmtCurrency = (num, currency = "INR") => {
  if (!num) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(num);
};

/* ─── Small reusable bits ─── */
const SectionLabel = ({ children }) => (
  <Text
    fontSize="xs"
    fontWeight="600"
    letterSpacing="wider"
    textTransform="uppercase"
    color="gray.400"
    mb={3}
  >
    {children}
  </Text>
);

const Field = ({ label, value, fullWidth }) => {
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.800", "gray.100");
  return (
    <Box gridColumn={fullWidth ? "1 / -1" : undefined}>
      <Text fontSize="xs" color={labelColor} mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="500" color={valueColor}>
        {value || "—"}
      </Text>
    </Box>
  );
};

const ChipList = ({ items, colorScheme = "blue" }) => {
  if (!items || items.length === 0)
    return (
      <Text fontSize="sm" color="gray.400">
        —
      </Text>
    );
  return (
    <Flex flexWrap="wrap" gap={2}>
      {items.map((item, i) => (
        <Tag
          key={i}
          size="sm"
          colorScheme={colorScheme}
          borderRadius="full"
          variant="subtle"
        >
          <TagLabel>{item}</TagLabel>
        </Tag>
      ))}
    </Flex>
  );
};

const BulletList = ({ items }) => {
  if (!items || items.length === 0)
    return (
      <Text fontSize="sm" color="gray.400">
        —
      </Text>
    );
  return (
    <VStack align="stretch" spacing={1.5}>
      {items.map((item, i) => (
        <HStack key={i} align="start" spacing={2}>
          <Box
            w="5px"
            h="5px"
            borderRadius="full"
            bg="blue.400"
            mt="6px"
            flexShrink={0}
          />
          <Text fontSize="sm">{item}</Text>
        </HStack>
      ))}
    </VStack>
  );
};

const ApprovalStep = ({ approval, index, isLast }) => {
  const statusColors = {
    Approved: { icon: CheckIcon, bg: "green.500", text: "green.600" },
    Rejected: { icon: CloseIcon, bg: "red.500", text: "red.600" },
    Pending: { icon: TimeIcon, bg: "gray.300", text: "gray.500" },
  };
  const cfg = statusColors[approval.status] || statusColors.Pending;
  return (
    <HStack align="start" spacing={3}>
      <VStack spacing={0} align="center">
        <Flex
          w="28px"
          h="28px"
          borderRadius="full"
          bg={cfg.bg}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={cfg.icon} color="white" boxSize="11px" />
        </Flex>
        {!isLast && <Box w="2px" flex={1} minH="24px" bg="gray.200" />}
      </VStack>
      <Box pb={isLast ? 0 : 4}>
        <Text fontSize="sm" fontWeight="600">
          {approval.approverName || "Approver"}
        </Text>
        <Text fontSize="xs" color="gray.400">
          {approval.approverRole || ""}
        </Text>
        {approval.comments && (
          <Text fontSize="xs" color="gray.500" mt={1} fontStyle="italic">
            "{approval.comments}"
          </Text>
        )}
        {approval.actionDate && (
          <Text fontSize="xs" color="gray.400" mt={0.5}>
            {fmt(approval.actionDate)}
          </Text>
        )}
      </Box>
    </HStack>
  );
};

const JobRequisitionDetailPage = () => {
  const { requisitionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [approvalComments, setApprovalComments] = useState("");
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const subtleText = useColorModeValue("gray.500", "gray.400");

  const canApprove = useMemo(() => {
    if (!requisition) return false;
    return (
      requisition.status === "Pending Approval" &&
      requisition.approvals?.some((a) => a.status === "Pending")
    );
  }, [requisition]);

  const canSubmit = requisition?.status === "Draft";
  const canEdit =
    requisition?.status === "Draft" || requisition?.status === "Rejected";

  const resolveRequisition = (res) =>
    res?.data?.requisition || res?.data || res?.requisition || res || null;

  const fetchRequisition = async () => {
    try {
      setLoading(true);
      const res = await jobRequisitionService.getRequisitionById(requisitionId);
      setRequisition(resolveRequisition(res));
    } catch {
      toast({
        title: "Failed to load requisition",
        status: "error",
        duration: 3000,
      });
      navigate("/recruiting/requisitions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisition();
  }, [requisitionId]); // eslint-disable-line

  const handleSubmitForApproval = async () => {
    try {
      setProcessing(true);
      await jobRequisitionService.submitForApproval(requisition._id);
      toast({
        title: "Submitted for approval",
        status: "success",
        duration: 2500,
      });
      fetchRequisition();
    } catch {
      toast({ title: "Could not submit", status: "error", duration: 3000 });
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessApproval = async (action) => {
    try {
      setProcessing(true);
      await jobRequisitionService.processApproval(
        requisition._id,
        action,
        approvalComments,
      );
      toast({
        title: `Requisition ${action}d`,
        status: "success",
        duration: 2500,
      });
      setApprovalComments("");
      fetchRequisition();
    } catch {
      toast({ title: `Could not ${action}`, status: "error", duration: 3000 });
    } finally {
      setProcessing(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setAddingComment(true);
      await jobRequisitionService.addComment(
        requisition._id,
        newComment.trim(),
      );
      setNewComment("");
      fetchRequisition();
    } catch {
      toast({
        title: "Could not add comment",
        status: "error",
        duration: 3000,
      });
    } finally {
      setAddingComment(false);
    }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <Flex h="60vh" align="center" justify="center" direction="column" gap={3}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
        <Text fontSize="sm" color={subtleText}>
          Loading requisition…
        </Text>
      </Flex>
    );
  }

  if (!requisition) {
    return (
      <Flex h="60vh" align="center" justify="center" direction="column" gap={3}>
        <Text>Requisition not found.</Text>
        <Button size="sm" onClick={() => navigate("/recruiting/requisitions")}>
          Go back
        </Button>
      </Flex>
    );
  }

  const statusCfg = getStatusColor(requisition.status);
  const priorityCfg = getPriorityColor(requisition.priority);
  const approvals = requisition.approvals || [];
  const comments = requisition.comments || [];

  return (
    <Box minH="100vh" bg={bg} pb={16}>
      {/* ── Header ── */}
      <Box
        bg={cardBg}
        borderBottom="1px solid"
        borderColor={border}
        px={{ base: 4, md: 8 }}
        py={4}
      >
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          color={subtleText}
          px={0}
          mb={3}
          _hover={{ color: "blue.500" }}
          onClick={() => navigate("/recruiting/requisitions")}
        >
          Requisitions
        </Button>

        <Flex align="start" justify="space-between" gap={4} flexWrap="wrap">
          <Box>
            <HStack spacing={2} mb={1}>
              <Badge
                px={2}
                py={0.5}
                borderRadius="full"
                fontSize="xs"
                fontWeight="600"
                bg={useColorModeValue(statusCfg.bg, "whiteAlpha.200")}
                color={statusCfg.color}
              >
                {requisition.status}
              </Badge>
              <Badge
                colorScheme={priorityCfg.scheme}
                variant="subtle"
                borderRadius="full"
                fontSize="xs"
              >
                {requisition.priority} Priority
              </Badge>
              <Text fontSize="xs" color={subtleText} fontFamily="mono">
                {requisition.requisitionId}
              </Text>
            </HStack>
            <Text
              fontSize="2xl"
              fontWeight="700"
              letterSpacing="-0.02em"
              lineHeight="1.2"
            >
              {requisition.jobTitle}
            </Text>
            <HStack spacing={4} mt={1.5} color={subtleText} fontSize="sm">
              {requisition.department && (
                <HStack spacing={1}>
                  <Icon as={FiBriefcase} boxSize="13px" />
                  <Text>{requisition.department}</Text>
                </HStack>
              )}
              {requisition.location && (
                <HStack spacing={1}>
                  <Icon as={FiMapPin} boxSize="13px" />
                  <Text>{requisition.location}</Text>
                </HStack>
              )}
              {requisition.numberOfPositions && (
                <HStack spacing={1}>
                  <Icon as={FiUsers} boxSize="13px" />
                  <Text>
                    {requisition.numberOfPositions} position
                    {requisition.numberOfPositions > 1 ? "s" : ""}
                  </Text>
                </HStack>
              )}
            </HStack>
          </Box>

          {canEdit && (
            <Button
              leftIcon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              variant="outline"
              onClick={() =>
                navigate(`/recruiting/requisitions/${requisition._id}/edit`)
              }
            >
              Edit
            </Button>
          )}
        </Flex>
      </Box>

      {/* ── Body ── */}
      <Box px={{ base: 4, md: 8 }} py={6}>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 320px" }} gap={6}>
          {/* ── Main Content ── */}
          <VStack spacing={5} align="stretch">
            {/* Position Details */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              p={5}
            >
              <SectionLabel>Position Details</SectionLabel>
              <Grid
                templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)" }}
                gap={4}
              >
                <Field label="Department" value={requisition.department} />
                <Field label="Location" value={requisition.location} />
                <Field
                  label="Employment Type"
                  value={requisition.employmentType}
                />
                <Field label="Job Level" value={requisition.jobLevel} />
                <Field
                  label="No. of Positions"
                  value={requisition.numberOfPositions}
                />
                <Field
                  label="Reporting Manager"
                  value={
                    typeof requisition.reportingManager === "object"
                      ? requisition.reportingManager?.name ||
                        requisition.reportingManager?.firstName
                      : requisition.reportingManager
                  }
                />
              </Grid>
            </Box>

            {/* Experience & Compensation */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              p={5}
            >
              <SectionLabel>Experience & Compensation</SectionLabel>
              <Grid
                templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
                gap={4}
              >
                <Field
                  label="Min Experience"
                  value={
                    requisition.experienceRequired?.min != null
                      ? `${requisition.experienceRequired.min} yrs`
                      : null
                  }
                />
                <Field
                  label="Max Experience"
                  value={
                    requisition.experienceRequired?.max != null
                      ? `${requisition.experienceRequired.max} yrs`
                      : null
                  }
                />
                <Field
                  label="Preferred"
                  value={requisition.experienceRequired?.preferred || null}
                />
                <Field
                  label="Budget Allocated"
                  value={fmtCurrency(
                    requisition.budgetAllocated,
                    requisition.salaryRange?.currency,
                  )}
                />
              </Grid>
              {(requisition.salaryRange?.min ||
                requisition.salaryRange?.max) && (
                <Box mt={4}>
                  <Text fontSize="xs" color={subtleText} mb={1}>
                    Salary Range
                  </Text>
                  <HStack spacing={1} align="center">
                    <Icon as={FiDollarSign} boxSize="13px" color={subtleText} />
                    <Text fontSize="sm" fontWeight="600">
                      {fmtCurrency(
                        requisition.salaryRange.min,
                        requisition.salaryRange.currency,
                      )}
                      {" – "}
                      {fmtCurrency(
                        requisition.salaryRange.max,
                        requisition.salaryRange.currency,
                      )}
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      {requisition.salaryRange.currency}
                    </Text>
                  </HStack>
                </Box>
              )}
            </Box>

            {/* Skills */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              p={5}
            >
              <SectionLabel>Skills Required</SectionLabel>
              <ChipList items={requisition.skills} colorScheme="blue" />
            </Box>

            {/* Qualifications */}
            {(requisition.qualifications || []).length > 0 && (
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={border}
                borderRadius="xl"
                p={5}
              >
                <SectionLabel>Qualifications</SectionLabel>
                <ChipList
                  items={requisition.qualifications}
                  colorScheme="purple"
                />
              </Box>
            )}

            {/* Responsibilities */}
            {(requisition.responsibilities || []).length > 0 && (
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={border}
                borderRadius="xl"
                p={5}
              >
                <SectionLabel>Responsibilities</SectionLabel>
                <BulletList items={requisition.responsibilities} />
              </Box>
            )}

            {/* Business Justification */}
            {requisition.businessJustification && (
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={border}
                borderRadius="xl"
                p={5}
              >
                <SectionLabel>Business Justification</SectionLabel>
                <Text
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  lineHeight="1.7"
                  color={useColorModeValue("gray.700", "gray.200")}
                >
                  {requisition.businessJustification}
                </Text>
              </Box>
            )}

            {/* Comments */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              p={5}
            >
              <SectionLabel>Comments</SectionLabel>
              {comments.length === 0 && (
                <Text fontSize="sm" color={subtleText} mb={4}>
                  No comments yet.
                </Text>
              )}
              <VStack align="stretch" spacing={4} mb={4}>
                {comments.map((c, i) => (
                  <HStack key={i} align="start" spacing={3}>
                    <Avatar
                      size="xs"
                      name={c.author?.name || c.authorName || "User"}
                      mt={0.5}
                    />
                    <Box flex={1}>
                      <HStack spacing={2} mb={0.5}>
                        <Text fontSize="xs" fontWeight="600">
                          {c.author?.name || c.authorName || "User"}
                        </Text>
                        <Text fontSize="xs" color={subtleText}>
                          {fmt(c.createdAt || c.date)}
                        </Text>
                      </HStack>
                      <Text
                        fontSize="sm"
                        color={useColorModeValue("gray.700", "gray.200")}
                      >
                        {c.text || c.comment}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
              <HStack>
                <Textarea
                  size="sm"
                  borderRadius="lg"
                  placeholder="Add a comment…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  resize="none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                      handleAddComment();
                  }}
                />
                <Tooltip label="Send (Ctrl+Enter)">
                  <IconButton
                    icon={<Icon as={FiSend} />}
                    colorScheme="blue"
                    variant="ghost"
                    aria-label="Add comment"
                    isLoading={addingComment}
                    onClick={handleAddComment}
                  />
                </Tooltip>
              </HStack>
            </Box>
          </VStack>

          {/* ── Sidebar ── */}
          <VStack spacing={4} align="stretch">
            {/* Key Dates */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              p={5}
            >
              <SectionLabel>Key Dates</SectionLabel>
              <VStack align="stretch" spacing={3}>
                {[
                  {
                    label: "Posting Date",
                    value: fmt(requisition.postingDate),
                  },
                  {
                    label: "Closing Date",
                    value: fmt(requisition.closingDate),
                  },
                  {
                    label: "Expected Joining",
                    value: fmt(requisition.expectedJoiningDate),
                  },
                  { label: "Created", value: fmt(requisition.createdAt) },
                ].map(({ label, value }) => (
                  <Flex key={label} justify="space-between" align="center">
                    <HStack spacing={1.5} color={subtleText}>
                      <Icon as={FiCalendar} boxSize="12px" />
                      <Text fontSize="xs">{label}</Text>
                    </HStack>
                    <Text fontSize="xs" fontWeight="500">
                      {value}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>

            {/* Workflow Actions */}
            {(canSubmit || canApprove) && (
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={border}
                borderRadius="xl"
                p={5}
              >
                <SectionLabel>Workflow</SectionLabel>
                {canSubmit && (
                  <Button
                    w="full"
                    colorScheme="blue"
                    size="sm"
                    onClick={handleSubmitForApproval}
                    isLoading={processing}
                  >
                    Submit for Approval
                  </Button>
                )}
                {canApprove && (
                  <VStack align="stretch" spacing={3}>
                    <Textarea
                      size="sm"
                      borderRadius="lg"
                      placeholder="Comments (optional)…"
                      value={approvalComments}
                      onChange={(e) => setApprovalComments(e.target.value)}
                      rows={3}
                      resize="none"
                    />
                    <HStack>
                      <Button
                        flex={1}
                        size="sm"
                        colorScheme="green"
                        leftIcon={<CheckIcon />}
                        onClick={() => handleProcessApproval("approve")}
                        isLoading={processing}
                      >
                        Approve
                      </Button>
                      <Button
                        flex={1}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        leftIcon={<CloseIcon />}
                        onClick={() => handleProcessApproval("reject")}
                        isLoading={processing}
                      >
                        Reject
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </Box>
            )}

            {/* Approval Timeline */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              p={5}
            >
              <SectionLabel>Approval Timeline</SectionLabel>
              {approvals.length === 0 ? (
                <Text fontSize="sm" color={subtleText}>
                  No approvals yet.
                </Text>
              ) : (
                <VStack align="stretch" spacing={0}>
                  {approvals.map((approval, i) => (
                    <ApprovalStep
                      key={`${approval.approverName}-${i}`}
                      approval={approval}
                      index={i}
                      isLast={i === approvals.length - 1}
                    />
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </Grid>
      </Box>
    </Box>
  );
};

export default JobRequisitionDetailPage;
